import React, { useEffect, useMemo, useRef, useState } from 'react'

import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell } from 'recharts'
import { motion } from 'framer-motion'
import Confetti from 'react-confetti'

/**
 * Payload esperado (ejemplo):
 * {
 *   data: [
 *     { QuestionId: 7, CalculatedPointsByZone: { canels: 1, tecate: 1, tequis: 1 } },
 *     { QuestionId: 6, CalculatedPointsByZone: { canels: 1, tequis: 1 } },
 *     ...
 *   ],
 *   idTile: 3.1,
 *   isActiveWomensMode: false,
 *   type: "finalResults"
 * }
 */

/* =========================
 * 1) Constantes y mapeos
 * ========================= */

const MODES = {
  MALE: 'MALE',
  FEMALE: 'FEMALE',
}

// Definimos zonas con un slug (clave que llega en payload), etiqueta visible y modo.
const ZONES = [
  // Varones
  { slug: 'laboratorio tequis', label: 'Laboratorio Tequis', internal: 'Zona Laboratorio Tequis', mode: MODES.MALE },
  { slug: 'canels', label: 'Canels', internal: 'Zona Canels', mode: MODES.MALE },
  { slug: 'seguros el potosí', label: 'Seguros El Potosí', internal: 'Zona Seguros El Potosí', mode: MODES.MALE },
  { slug: 'red cola', label: 'Red Cola', internal: 'Zona Red Cola', mode: MODES.MALE },
  { slug: 'tecate', label: 'Tecate', internal: 'Zona Tecate', mode: MODES.MALE },
  { slug: 'moctezuma', label: 'Moctezuma', internal: 'Zona Moctezuma', mode: MODES.MALE },
  { slug: 'nissan', label: 'Nissan', internal: 'Zona Nissan', mode: MODES.MALE },
  { slug: 'mobil', label: 'Mobil', internal: 'Zona Mobil', mode: MODES.MALE },
  { slug: 'lounge ron potosí', label: 'Lounge Ron Potosí', internal: 'Zona Lounge Ron Potosí', mode: MODES.MALE },
  { slug: 'palcos', label: 'Palcos', internal: 'Zona Palcos', mode: MODES.MALE },
  // Femenil
  { slug: 'azul', label: 'Azul', internal: 'Zona Azul', mode: MODES.FEMALE },
  { slug: 'oro', label: 'Oro', internal: 'Zona Oro', mode: MODES.FEMALE },

  // Santos Agnóstico
  { slug: 'zona1', label: 'Santos Local', internal: 'Santos Local', mode: MODES.MALE },
  { slug: 'zona2', label: 'Santos Visitante', internal: 'Santos Visitante', mode: MODES.MALE },
  { slug: 'zona1', label: 'Santos Local', internal: 'Santos Local', mode: MODES.FEMALE },
  { slug: 'zona2', label: 'Santos Visitante', internal: 'Santos Visitante', mode: MODES.FEMALE },
]

// Utilidad: normaliza a slug comparable (lowercase sin tildes básicas)
const normalize = (s) =>
  (s ?? '')
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()

const mapToCanonicalSlug = (rawKey) => {
  const norm = normalize(rawKey).replace(/\s+/g, '')
  if (norm === 'zona1' || norm === 'santos1' || norm === 'santoslocal' || norm === 'teamsantoslocal' || norm === 'lucho' || norm === 'teamlucho') {
    return 'zona1'
  }
  if (norm === 'zona2' || norm === 'santos2' || norm === 'santosvisitante' || norm === 'teamsantosvisitante' || norm === 'tunita' || norm === 'teamtunita') {
    return 'zona2'
  }
  return normalize(rawKey)
}

/* =========================
 * 2) Utilidades puras
 * ========================= */

/**
 * Retorna arreglo de zonas del modo activo.
 */
function getModeZones(isActiveWomensMode) {
  const mode = isActiveWomensMode ? MODES.FEMALE : MODES.MALE
  return ZONES.filter((z) => z.mode === mode)
}

/**
 * Agrega puntos por zona (del modo activo) a partir del payload.
 * Retorna un mapa: { slug: totalPoints }
 */
function aggregatePointsByZone(payload, isActiveWomensMode) {
  const modeZones = getModeZones(isActiveWomensMode)
  const allowedSlugs = new Set(modeZones.map((z) => normalize(z.slug)))
  const result = {}
  for (const slug of allowedSlugs) result[slug] = 0

  const entries = Array.isArray(payload?.data) ? payload.data : []

  for (const item of entries) {
    const calc = item?.CalculatedPointsByZone || item?.calculatedPointsByZone

    if (!calc || typeof calc !== 'object') continue

    for (const [zoneKey, pts] of Object.entries(calc)) {
      const slug = mapToCanonicalSlug(zoneKey)
      if (allowedSlugs.has(slug)) {
        const val = Number(pts) || 0
        result[slug] = (result[slug] ?? 0) + val
      }
    }
  }
  return result
}

/**
 * Determina si NO hubo puntos calculados en ninguna pregunta del payload.
 * (i.e., todas las preguntas carecen de CalculatedPointsByZone o vienen vacías)
 */
function noCalculatedPointsAtAll(payload) {
  const entries = Array.isArray(payload?.data) ? payload.data : []
  
  if (entries.length === 0) return true

  return entries.every((item) => {
    const c = item?.CalculatedPointsByZone || item?.calculatedPointsByZone

    return !c || Object.keys(c).length === 0
  })
}

/**
 * Construye datos para la gráfica a partir del mapa { slug: points }.
 */
function buildChartData(pointsMap, isActiveWomensMode) {
  const modeZones = getModeZones(isActiveWomensMode)
  return modeZones.map((z) => ({
    slug: z.slug,
    name: z.label, // lo que verá el usuario en el eje X
    value: pointsMap[normalize(z.slug)] ?? 0,
  }))
}

/**
 * Genera datos aleatorios para animación (mismo shape que buildChartData).
 * El rango de valores se puede ajustar. Se usa un rango pequeño para “suspenso”.
 */
function randomChartData(isActiveWomensMode, max = 20) {
  const modeZones = getModeZones(isActiveWomensMode)
  return modeZones.map((z) => ({
    slug: z.slug,
    name: z.label,
    value: Math.floor(Math.random() * max) + 1,
  }))
}

// finalData: [{ slug, name, value }]
// totalZones: cantidad de zonas del modo activo (finalChartData.length)
/**
 * Calcula zonas ganadoras a partir de datos definitivos [{slug,name,value}].
 * Reglas especiales:
 *  - Si max = 0 → pick aleatoria (una sola ganadora).
 */
const computeWinners = (finalData, totalZones) => {
  if (!finalData?.length) {
    return { winners: [], allEqualPositive: false, pickedRandom: null, allZonesTied: false }
  }

  const values = finalData.map((d) => Number(d.value) || 0)
  const max = Math.max(...values)

  // Todos en 0 → elegir una al azar
  if (max === 0) {
    const randomIdx = Math.floor(Math.random() * finalData.length)
    const picked = finalData[randomIdx]
    return {
      winners: [picked],
      allEqualPositive: false,
      pickedRandom: picked,
      allZonesTied: false,
    }
  }

  const winners = finalData.filter((d) => Number(d.value) === max)

  // Empate total si ganadoras == total de zonas del modo activo y max > 0
  const allZonesTied = winners.length === totalZones && max > 0

  return { winners, allEqualPositive: allZonesTied, pickedRandom: null, allZonesTied }
}

/* =========================
 * 3) Hook de estado/flujo
 * ========================= */

function useResultsFlow(payload) {
  // Fases: 'animating' → 'reveal' → 'winner'
  const [phase, setPhase] = useState('animating')
  const [animatedData, setAnimatedData] = useState([])
  const [finalData, setFinalData] = useState([])
  const [winners, setWinners] = useState([])
  const [allEqualPositive, setAllEqualPositive] = useState(false)
  const [pickedRandom, setPickedRandom] = useState(null)
  const intervalRef = useRef(null)
  const revealTimeoutRef = useRef(null)
  const winnerTimeoutRef = useRef(null)
  const [allZonesTied, setAllZonesTied] = useState(false)

  const isActiveWomensMode = !!payload?.isActiveWomensMode

  // Cálculos estáticos derivados del payload
  const pointsMap = useMemo(() => aggregatePointsByZone(payload, isActiveWomensMode), [payload, isActiveWomensMode])
  const finalChartData = useMemo(() => buildChartData(pointsMap, isActiveWomensMode), [pointsMap, isActiveWomensMode])
  const noPointsAtAll = useMemo(() => noCalculatedPointsAtAll(payload), [payload])

  const totalZones = finalChartData.length


  // //--- Desarrollo ---//
  // useEffect(() => {
  //   // DEBUG (quítalo luego)
  //   console.log({
  //     phase,
  //     isActiveWomensMode,
  //     animatedData,
  //     finalData,
  //     winners,
  //     allZonesTied,
  //     pointsMap,
  //     noPointsAtAll
  //     //noPointsAtAll: noPointsAtAll(payload)
  //   })
  // }, [phase, animatedData, finalData, winners, allZonesTied, isActiveWomensMode])
  // //--- Desarrollo ---//
  

  useEffect(() => {
    // Si no hay zonas para el modo, salimos
    if (finalChartData.length === 0) {
      setPhase('winner')
      setAnimatedData([])
      setFinalData([])
      setWinners([])
      setAllEqualPositive(false)
      setPickedRandom(null)
      return
    }

    // 1) Empezar animación (valores aleatorios)
    setPhase('animating')
    setAnimatedData(randomChartData(isActiveWomensMode))
    intervalRef.current = setInterval(() => {
      setAnimatedData(randomChartData(isActiveWomensMode))
    }, 120)

    // 2) A los ~3s, revelar valores reales
    revealTimeoutRef.current = setTimeout(() => {
      clearInterval(intervalRef.current)
      setAnimatedData(finalChartData)
      setFinalData(finalChartData)
      setPhase('reveal')

      // 3) Breve pausa y luego calcular ganadores
      winnerTimeoutRef.current = setTimeout(() => {
        // Regla especial: si no hubo puntos calculados en ninguna pregunta → “Ganaron todas”
        if (noPointsAtAll) {
          // console.log('[Winners check]', {
          //   totalZones,
          //   finalData: finalChartData,
          //   winners,
          //   allZonesTied,
          //   noPointsAtAll
          // })

          setWinners(finalChartData) // todas ganan
          setAllEqualPositive(true)  // para el mensaje “¡Ganaron todas…!”
          setAllZonesTied(true)
          setPickedRandom(null)
          setPhase('winner')
          return
        }

        // const { winners, allEqualPositive, pickedRandom, allZonesTied } = computeWinners(finalChartData)
        // setWinners(winners)
        // setAllEqualPositive(allEqualPositive)
        // setPickedRandom(pickedRandom)
        // setAllZonesTied(allZonesTied)
        // setPhase('winner')

        const { winners, allEqualPositive, pickedRandom, allZonesTied } = computeWinners(finalChartData, totalZones)

        //// DEBUG — imprime SIEMPRE el resultado final del cómputo
        // console.log('[Winners check FINAL]', {
        //   totalZones,
        //   finalData: finalChartData,
        //   winners,
        //   allZonesTied
        // })

        setWinners(winners)
        setAllEqualPositive(allEqualPositive)
        setPickedRandom(pickedRandom)
        setAllZonesTied(allZonesTied)
        setPhase('winner')
      }, 500)
    }, 3000)

    // Limpieza al desmontar o al cambiar el payload
    return () => {
      clearInterval(intervalRef.current)
      clearTimeout(revealTimeoutRef.current)
      clearTimeout(winnerTimeoutRef.current)
    }
  }, [payload, isActiveWomensMode, finalChartData, noPointsAtAll])

  return { phase, animatedData, finalData, winners, allEqualPositive, pickedRandom, isActiveWomensMode, allZonesTied }
}

/* =========================
 * 4) Componente principal
 * ========================= */

const Results = ({ payload }) => {
  //const { phase, animatedData, finalData, winners, allEqualPositive, pickedRandom, isActiveWomensMode, allZonesTied } = useResultsFlow(payload)
  //const { phase, animatedData, winners, pickedRandom, allZonesTied } = useResultsFlow(payload)
  const { phase, animatedData, finalData, winners, pickedRandom, allZonesTied, isActiveWomensMode } = useResultsFlow(payload)



  // //--- Desarrollo ---//
  // useEffect(() => {
  //   // DEBUG — quítalo cuando termines
  //   console.log('[Results Component]', {
  //     phase,
  //     animatedData,
  //     finalData,
  //     winners,
  //     pickedRandom,
  //     allZonesTied,
  //     isActiveWomensMode
  //   })
  // }, [phase, animatedData, finalData, winners, pickedRandom, allZonesTied, isActiveWomensMode])
  // //--- Desarrollo ---//




  const showChart = phase === 'animating' || phase === 'reveal'
  const showWinner = phase === 'winner' && winners.length > 0

  // Tamaño seguro para Confetti
  const width = typeof window !== 'undefined' ? window.innerWidth : 1024
  const height = typeof window !== 'undefined' ? window.innerHeight : 768


  useEffect(() => {
    if (showChart) {
      requestAnimationFrame(() => window.dispatchEvent(new Event('resize')))
    }
  }, [showChart])





  // Paletas por modo (elige tonos buenos en negro)
  const PALETTE_MALE = {
    'laboratorio tequis': '#4CC9F0',
    canels: '#F72585',
    'seguros el potosí': '#B5179E',
    'red cola': '#F94144',
    tecate: '#F3722C',
    moctezuma: '#90BE6D',
    nissan: '#F9C74F',
    mobil: '#43AA8B',
    'lounge ron potosí': '#7f43aaff'
  }

  const PALETTE_FEMALE = {
    azul: '#60A5FA', // azul
    oro: '#F59E0B'   // oro
  }

  function getZoneColor(slug, isActiveWomensMode) {
    const key = normalize(slug)
    const palette = isActiveWomensMode ? PALETTE_FEMALE : PALETTE_MALE
    return palette[key] || '#8B5CF6' // fallback
  }





  return (
    <div className="Results__FullScreenOverlay">
      {/* 📊 Gráfica (animación → revelado) */}
      {/* {showChart && animatedData.length > 0 && (
        <div style={{ minWidth: 0, minHeight: 0 /* altura controlada por aspect / }}>
          <ResponsiveContainer width="100%" height="100%" aspect={2}>
            <BarChart data={animatedData}>
              <CartesianGrid stroke="#3a3a3a" /* grid tenue / strokeDasharray="3 3" />
              <XAxis
                dataKey="name"
                tick={{ fill: '#eaeaea' }}
                axisLine={{ stroke: '#888' }}
                tickLine={{ stroke: '#888' }}
              />
              <YAxis
                tick={{ fill: '#eaeaea' }}
                axisLine={{ stroke: '#888' }}
                tickLine={{ stroke: '#888' }}
              />
              <Tooltip
                contentStyle={{ background: '#1f1f1f', border: '1px solid #444', color: '#fff' }}
                labelStyle={{ color: '#bbb' }}
                itemStyle={{ color: '#fff' }}
                formatter={(value) => [`${value} pts`, 'Zona']}
              />
              {/* Una barra con color por zona /}
              <Bar
                dataKey="value"
                isAnimationActive={true}
                animationDuration={400}
                // color dinámica por zona:
                fillOpacity={0.9}
              >
                {
                  // Si quieres colorear cada barra según el slug:
                  animatedData.map((entry, idx) => (
                    <cell
                      // en JSX real: usa <Cell />, aquí el tag es con mayúscula
                      /* eslint-disable jsx-a11y/aria-role /
                      key={`cell-${idx}`}
                      fill={getZoneColor(entry.slug, isActiveWomensMode)}
                    />
                  ))
                }
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )} */}

      {/* 📊 Gráfica (animación → revelado) */}
      {showChart && animatedData.length > 0 && (
        // <div className="w-full h-96">
        <div style={{ minWidth: 0, minHeight: 0, height: '24rem' }}>
          <ResponsiveContainer width="100%" height="100%" aspect={4}>
            <BarChart data={animatedData}>
              <CartesianGrid stroke="#3a3a3a" /* grid tenue */ strokeDasharray="3 3" />
              {/* <XAxis dataKey="name" tick={{ fill: '#eaeaea' }} axisLine={{ stroke: '#888' }} tickLine={{ stroke: '#888' }} /> */}
              {/* <YAxis tick={{ fill: '#eaeaea' }} axisLine={{ stroke: '#888' }} tickLine={{ stroke: '#888' }} /> */}
              {/* <Tooltip contentStyle={{ background: '#1f1f1f', border: '1px solid #444', color: '#fff' }} labelStyle={{ color: '#bbb' }} itemStyle={{ color: '#fff' }} formatter={(value) => [`${value} pts`, 'Zona']} /> */}
              {/* <Bar dataKey="value" fill="#60A5FA" /> */}
              <Bar dataKey="value" isAnimationActive animationDuration={400} fillOpacity={0.95}>
                {animatedData.map((entry, idx) => (
                  <Cell key={`cell-${idx}`} fill={getZoneColor(entry.slug, isActiveWomensMode)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* 🏆 Resultado */}
      {showWinner && (
        <>
          <Confetti width={width} height={height} recycle={false} numberOfPieces={300} />
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1.12 }}
            transition={{ duration: 0.6, type: 'spring' }}
            className="text-4xl font-bold text-center mt-6"
          >
            <p><i className="bi bi-trophy fs-1"></i></p>

            {/* Mensajería de ganadores */}
            {(() => {
              if (allZonesTied) {
                {/* ¡Han empatado todas las zonas! */}
                return <p className="Results__FullScreenOverlay__LargeWinner">¡Ha habido un empate total!</p>
              }

              // Cuando ninguno respondió, se elegía uno al azar, pero lo cambié para que muestre un empate
              if (pickedRandom) {
                // return (
                //   <p className="Results__FullScreenOverlay__Title">
                //     Nadie anotó… elegimos al azar y la zona ganadora es:<br />
                //     Zona {pickedRandom.name}
                //   </p>
                // )
                {/* ¡Han empatado todas las zonas! */}
                return <p className="Results__FullScreenOverlay__LargeWinner">¡Ha habido un empate total!</p>
              }

              if (winners) {
                if (winners.length === 0) {
                  return (
                    <p className="Results__FullScreenOverlay__OneWinner">¡Ha habido un empate total!</p>
                  )
                }
  
                if (winners.length === 1) {
                  return (
                    <p className="Results__FullScreenOverlay__OneWinner">
                      La zona ganadora es…<br />
                      Zona {winners[0].name}
                    </p>
                  )
                }
  
                return (
                  <>
                    <p className="Results__FullScreenOverlay__MultipleWinner">¡Empate! Ganaron las zonas:</p>
                    {winners.map((w) => (
                      <p key={w.slug} className="Results__FullScreenOverlay__MultipleWinner">Zona {w.name}</p>
                    ))}
                  </>
                )
              } else {
                return (
                  <p className="Results__FullScreenOverlay__OneWinner">¡Ha habido un empate total!</p>
                )
              }
            })()}
          </motion.div>
        </>
      )}
    </div>
  )
}

// const Results = ({ payload }) => {
//   const [animatedData, setAnimatedData] = useState([])
//   const [finalResults, setFinalResults] = useState([])
//   const [showWinner, setShowWinner] = useState(false)
//   const [winningZones, setWinningZones] = useState([])
//   const [showChart, setShowChart] = useState(true)


//   const modes = {
//     MALE: MALE,
//     FEMALE: FEMALE
//   }

//   const zones = [
//     {
//       name: "Zona Laboratorio Tequis",
//       text: "tequis",
//       points: 0,
//       mode: modes.MALE
//     },
//     {
//       name: "Zona Canels",
//       text: "canels",
//       points: 0,
//       mode: modes.MALE
//     },
//     {
//       name: "Zona Seguros El Potosí",
//       text: "seguros el potosí",
//       points: 0,
//       mode: modes.MALE
//     },
//     {
//       name: "Zona Red Cola",
//       text: "red cola",
//       points: 0,
//       mode: modes.MALE
//     },
//     {
//       name: "Zona Tecate",
//       text: "tecate",
//       points: 0,
//       mode: modes.MALE
//     },
//     {
//       name: "Zona Moctezuma",
//       text: "moctezuma",
//       points: 0,
//       mode: modes.MALE
//     },
//     {
//       name: "Zona Nissan",
//       text: "nissan",
//       points: 0,
//       mode: modes.MALE
//     },
//     {
//       name: "Zona Mobil",
//       text: "mobil",
//       points: 0,
//       mode: modes.MALE
//     },
//     {
//       name: "Zona Azul",
//       text: "azul",
//       points: 0,
//       mode: modes.FEMALE
//     },
//     {
//       name: "Zona Oro",
//       text: "oro",
//       points: 0,
//       mode: modes.FEMALE
//     }
//   ]


//   const GetResults = async () => {
//     try {

//       if (payload.isActiveWomensMode) {

//       } else {

//       }
//     } catch (error) {
      
//     }
//   }



//   // // Generar datos aleatorios para animación
//   // const generateAnimatedData = (zones) => {
//   //   debugger
//   //   return zones.map(zone => ({
//   //     name: zone.name,
//   //     value: Math.floor(Math.random() * 20) + 1
//   //   }))
//   // }


//   useEffect(() => {
//     // 1️⃣ Extraemos los datos reales al cargar
//     const realResults = extractFinalResults(payload)
//     setFinalResults(realResults)

//     // 2️⃣ Animación con datos aleatorios
//     let intervalId = null
//     if (realResults.length > 0) {
//       setAnimatedData(generateAnimatedData(realResults)) // inicia animación
//       intervalId = setInterval(() => {
//         setAnimatedData(generateAnimatedData(realResults))
//       }, 100)
//     }
    
//     // 3️⃣ Después de 3 segundos, se muestra el resultado real
//     const revealTimeout = setTimeout(() => {
//       clearInterval(intervalId)
//       setAnimatedData(realResults) // actualiza gráfica con datos reales
      
//       const showWinnerTimeout = setTimeout(() => {
//         setShowChart(false)
        
//         const winners = getWinningZones(realResults) // asegúrate de usar datos REALES
        
//         setWinningZones(winners)
//         setShowWinner(true)
//       }, 500)

//       return () => clearTimeout(showWinnerTimeout)
//     }, 3000)
    
//     // Cleanup en desmontaje
//     return () => {
//       clearInterval(intervalId)
//       clearTimeout(revealTimeout)
//     }
//   }, [payload])


//   return (
//     <div className="Results__FullScreenOverlay">
//       {/* 📊 Gráfica animada */}
//       {showChart && (
//         <div className="w-full h-96">
//           <ResponsiveContainer width="100%" height="100%">
//             <BarChart data={animatedData}>
//               <CartesianGrid strokeDasharray="3 3" stroke="#ccc" />
//               <XAxis dataKey="name" stroke="#fff" tickFormatter={getZoneLabel} />
//               <YAxis stroke="#fff" />
//               <Tooltip formatter={(value, name, props) => [`${value} pts`, getZoneLabel(props.payload.name)]} />
//               <Bar dataKey="value" fill="#ff4d4f" />
//             </BarChart>
//           </ResponsiveContainer>
//         </div>
//       )}

//       {/* 🏆 Ganador */}
//       {showWinner && winningZones.length > 0 && (
//         <>
//           <Confetti width={window.innerWidth} height={window.innerHeight} recycle={false} numberOfPieces={300} />
//           <motion.div
//             initial={{ scale: 0 }}
//             animate={{ scale: 1.2 }}
//             transition={{ duration: 0.6, type: 'spring' }}
//             className="text-4xl font-bold text-center mt-4"
//           >
//             <p><i className="bi bi-trophy fs-1"></i></p>

//             {(() => {
//               const allHaveSamePoints = finalResults.every(z => z.value === finalResults[0].value)
//               const noZeros = finalResults.every(z => z.value > 0)

//               if (winningZones.length === 1) {
//                 // 🎯 Solo una zona con puntaje máximo
//                 return (
//                   <p className='fs-1'>
//                     La zona ganadora es...<br />
//                     Zona {getZoneLabel(winningZones[0].name)}
//                   </p>
//                 )
//               } else if (allHaveSamePoints && noZeros) {
//                 // 🟰 Empate total real (todas con mismo puntaje > 0)
//                 return (
//                   <p className='fs-1'>¡Ganaron todas las zonas!</p>
//                 )
//               } else {
//                 // 🤝 Empate entre algunas zonas
//                 return (
//                   <>
//                     <p className='fs-1'>Ganaron las zonas:</p>
//                     {winningZones.map((w, i) => (
//                       <p key={i} className='fs-1'>Zona {getZoneLabel(w.name)}</p>
//                     ))}
//                   </>
//                 )
//               }
//             })()}
//           </motion.div>
//         </>
//       )}
//     </div>
//   )
// }

export { Results };









// // 📌 Diccionario para traducir nombres internos a nombres amigables
// const zonesTemplate = [
//   { name: "Zona Laboratorio Tequis", text: "Tequis" },
//   { name: "Zona Canels", text: "Canels" },
//   { name: "Zona Seguros El Potosí", text: "Seguros El Potosí" },
//   { name: "Zona Red Cola", text: "Red Cola" },
//   { name: "Zona Tecate", text: "Tecate" },
//   { name: "Zona Moctezuma", text: "Moctezuma" },
//   { name: "Zona Nissan", text: "Nissan" },
//   { name: "Zona Mobil", text: "Mobil" },
//   { name: "Zona Azul", text: "Azul" },
//   { name: "Zona Oro", text: "Oro" }
// ]

// // 🔍 Buscar nombre amigable a partir del nombre interno
// const getZoneLabel = (zoneName) => {
//   debugger
//   const match = zonesTemplate.find(z =>
//     z.name.toLowerCase().includes(zoneName.toLowerCase())
//   )
//   return match ? match.text : zoneName
// }

// // 🎯 Extraer datos reales del payload
// const extractFinalResults = (payload) => {
//   debugger
//   const points = payload?.data?.[0]?.CalculatedPointsByZone || {}
//   return Object.entries(points).map(([name, value]) => ({ name, value }))
// }

// // 🎲 Generar datos aleatorios para animación
// const generateAnimatedData = (zones) => {
//   debugger
//   return zones.map(zone => ({
//     name: zone.name,
//     value: Math.floor(Math.random() * 20) + 1
//   }))
// }

// // 🏆 Obtener zonas con el valor más alto
// const getWinningZones = (zones) => {
//   debugger
//   if (!zones || zones.length === 0) return []
//   const maxPoints = Math.max(...zones.map(z => z.value))
//   return zones.filter(z => z.value === maxPoints)
// }

// // 💡 Componente principal
// const Results = ({ payload }) => {
//   const [animatedData, setAnimatedData] = useState([])
//   const [finalResults, setFinalResults] = useState([])
//   const [showWinner, setShowWinner] = useState(false)
//   const [winningZones, setWinningZones] = useState([])
//   const [showChart, setShowChart] = useState(true)

//   useEffect(() => {
//     // 1️⃣ Extraemos los datos reales al cargar
//     const realResults = extractFinalResults(payload)
//     setFinalResults(realResults)
//     debugger

//     // 2️⃣ Animación con datos aleatorios
//     let intervalId = null
//     if (realResults.length > 0) {
//       setAnimatedData(generateAnimatedData(realResults)) // inicia animación
//       intervalId = setInterval(() => {
//         setAnimatedData(generateAnimatedData(realResults))
//       }, 100)
//     }
//     debugger
//     // 3️⃣ Después de 3 segundos, se muestra el resultado real
//     const revealTimeout = setTimeout(() => {
//       debugger
//       clearInterval(intervalId)
//       setAnimatedData(realResults) // actualiza gráfica con datos reales
//       debugger
//       const showWinnerTimeout = setTimeout(() => {
//         setShowChart(false)
//         debugger
//         const winners = getWinningZones(realResults) // asegúrate de usar datos REALES
//         debugger
//         setWinningZones(winners)
//         setShowWinner(true)
//       }, 500)

//       return () => clearTimeout(showWinnerTimeout)
//     }, 3000)
//     debugger
//     // Cleanup en desmontaje
//     return () => {
//       clearInterval(intervalId)
//       clearTimeout(revealTimeout)
//     }
//   }, [payload])

//   return (
//     <div className="Results__FullScreenOverlay">
//       {/* 📊 Gráfica animada */}
//       {showChart && (
//         <div className="w-full h-96">
//           <ResponsiveContainer width="100%" height="100%">
//             <BarChart data={animatedData}>
//               <CartesianGrid strokeDasharray="3 3" stroke="#ccc" />
//               <XAxis dataKey="name" stroke="#fff" tickFormatter={getZoneLabel} />
//               <YAxis stroke="#fff" />
//               <Tooltip formatter={(value, name, props) => [`${value} pts`, getZoneLabel(props.payload.name)]} />
//               <Bar dataKey="value" fill="#ff4d4f" />
//             </BarChart>
//           </ResponsiveContainer>
//         </div>
//       )}

//       {/* 🏆 Ganador */}
//       {showWinner && winningZones.length > 0 && (
//         <>
//           <Confetti width={window.innerWidth} height={window.innerHeight} recycle={false} numberOfPieces={300} />
//           <motion.div
//             initial={{ scale: 0 }}
//             animate={{ scale: 1.2 }}
//             transition={{ duration: 0.6, type: 'spring' }}
//             className="text-4xl font-bold text-center mt-4"
//           >
//             <p><i className="bi bi-trophy fs-1"></i></p>

//             {(() => {
//               const allHaveSamePoints = finalResults.every(z => z.value === finalResults[0].value)
//               const noZeros = finalResults.every(z => z.value > 0)

//               if (winningZones.length === 1) {
//                 // 🎯 Solo una zona con puntaje máximo
//                 return (
//                   <p className='fs-1'>
//                     La zona ganadora es...<br />
//                     Zona {getZoneLabel(winningZones[0].name)}
//                   </p>
//                 )
//               } else if (allHaveSamePoints && noZeros) {
//                 // 🟰 Empate total real (todas con mismo puntaje > 0)
//                 return (
//                   <p className='fs-1'>¡Ganaron todas las zonas!</p>
//                 )
//               } else {
//                 // 🤝 Empate entre algunas zonas
//                 return (
//                   <>
//                     <p className='fs-1'>Ganaron las zonas:</p>
//                     {winningZones.map((w, i) => (
//                       <p key={i} className='fs-1'>Zona {getZoneLabel(w.name)}</p>
//                     ))}
//                   </>
//                 )
//               }
//             })()}
//           </motion.div>
//         </>
//       )}
//     </div>
//   )
// }









































































// import React, { useEffect, useState } from 'react'
// import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
// import { motion } from 'framer-motion'
// import Confetti from 'react-confetti'


// // 📌 Diccionario para traducir nombres internos a nombres amigables
// const zonesTemplate = [
//   { name: "Zona Laboratorio Tequis", text: "Tequis" },
//   { name: "Zona Canels", text: "Canels" },
//   { name: "Zona Seguros El Potosí", text: "Seguros El Potosí" },
//   { name: "Zona Red Cola", text: "Red Cola" },
//   { name: "Zona Tecate", text: "Tecate" },
//   { name: "Zona Moctezuma", text: "Moctezuma" },
//   { name: "Zona Nissan", text: "Nissan" },
//   { name: "Zona Mobil", text: "Mobil" },
//   { name: "Zona Azul", text: "Azul" },
//   { name: "Zona Oro", text: "Oro" }
// ]

// // 🔍 Buscar nombre amigable a partir del nombre interno
// const getZoneLabel = (zoneName) => {
//   const match = zonesTemplate.find(z =>
//     z.name.toLowerCase().includes(zoneName.toLowerCase())
//   )
//   return match ? match.text : zoneName
// }

// // 🎯 Extraer datos reales del payload
// const extractFinalResults = (payload) => {
//   const points = payload?.data?.[0]?.CalculatedPointsByZone || {}
//   return Object.entries(points).map(([name, value]) => ({ name, value }))
// }

// // 🎲 Generar datos aleatorios para animación
// const generateAnimatedData = (zones) => {
//   return zones.map(zone => ({
//     name: zone.name,
//     value: Math.floor(Math.random() * 20) + 1
//   }))
// }

// // 🏆 Obtener zonas con el valor más alto
// const getWinningZones = (zones) => {
//   if (!zones || zones.length === 0) return []
//   const maxPoints = Math.max(...zones.map(z => z.value))
//   return zones.filter(z => z.value === maxPoints)
// }

// // 💡 Componente principal
// const Results = ({ payload }) => {
//   const [animatedData, setAnimatedData] = useState([])
//   const [finalResults, setFinalResults] = useState([])
//   const [showWinner, setShowWinner] = useState(false)
//   const [winningZones, setWinningZones] = useState([])
//   const [showChart, setShowChart] = useState(true)

//   useEffect(() => {
//     // 1️⃣ Extraemos los datos reales al cargar
//     const realResults = extractFinalResults(payload)
//     setFinalResults(realResults)

//     // 2️⃣ Animación con datos aleatorios
//     let intervalId = null
//     if (realResults.length > 0) {
//       setAnimatedData(generateAnimatedData(realResults)) // inicia animación
//       intervalId = setInterval(() => {
//         setAnimatedData(generateAnimatedData(realResults))
//       }, 100)
//     }

//     // 3️⃣ Después de 3 segundos, se muestra el resultado real
//     const revealTimeout = setTimeout(() => {
//       clearInterval(intervalId)
//       setAnimatedData(realResults) // actualiza gráfica con datos reales

//       const showWinnerTimeout = setTimeout(() => {
//         setShowChart(false)
//         const winners = getWinningZones(realResults) // asegúrate de usar datos REALES
//         setWinningZones(winners)
//         setShowWinner(true)
//       }, 500)

//       return () => clearTimeout(showWinnerTimeout)
//     }, 3000)

//     // Cleanup en desmontaje
//     return () => {
//       clearInterval(intervalId)
//       clearTimeout(revealTimeout)
//     }
//   }, [payload])

//   return (
//     <div className="Results__FullScreenOverlay">
//       {/* 📊 Gráfica animada */}
//       {showChart && (
//         <div className="w-full h-96">
//           <ResponsiveContainer width="100%" height="100%">
//             <BarChart data={animatedData}>
//               <CartesianGrid strokeDasharray="3 3" stroke="#ccc" />
//               <XAxis dataKey="name" stroke="#fff" tickFormatter={getZoneLabel} />
//               <YAxis stroke="#fff" />
//               <Tooltip formatter={(value, name, props) => [`${value} pts`, getZoneLabel(props.payload.name)]} />
//               <Bar dataKey="value" fill="#ff4d4f" />
//             </BarChart>
//           </ResponsiveContainer>
//         </div>
//       )}

//       {/* 🏆 Ganador */}
//       {showWinner && winningZones.length > 0 && (
//         <>
//           <Confetti width={window.innerWidth} height={window.innerHeight} recycle={false} numberOfPieces={300} />
//           <motion.div
//             initial={{ scale: 0 }}
//             animate={{ scale: 1.2 }}
//             transition={{ duration: 0.6, type: 'spring' }}
//             className="text-4xl font-bold text-center mt-4"
//           >
//             <p><i className="bi bi-trophy fs-1"></i></p>
//             {/* {(() => {
//               if (winningZones.length === 1) {
//                 // 🎯 Solo una zona con puntaje máximo
//                 return (
//                   <p className='fs-1'>
//                     La zona ganadora es...<br />
//                     Zona {getZoneLabel(winningZones[0].name)}
//                   </p>
//                 )
//               } else if (finalResults.every(z => z.value === finalResults[0].value)) {
//                 return (
//                   <p className='fs-1'>¡Ganaron todas las zonas!</p>
//                 )
//               } else {
//                 // 🤝 Empate entre algunas zonas
//                 return (
//                   <>
//                     <p className='fs-1'>Ganaron las zonas:</p>
//                     {winningZones.map((w, i) => (
//                       <p key={i} className='fs-1'>Zona {getZoneLabel(w.name)}</p>
//                     ))}
//                   </>
//                 )
//               }
//             })()} */}

//             {(() => {
//               const allHaveSamePoints = finalResults.every(z => z.value === finalResults[0].value)
//               const noZeros = finalResults.every(z => z.value > 0)

//               if (winningZones.length === 1) {
//                 // 🎯 Solo una zona con puntaje máximo
//                 return (
//                   <p className='fs-1'>
//                     La zona ganadora es...<br />
//                     Zona {getZoneLabel(winningZones[0].name)}
//                   </p>
//                 )
//               } else if (allHaveSamePoints && noZeros) {
//                 // 🟰 Empate total real (todas con mismo puntaje > 0)
//                 return (
//                   <p className='fs-1'>¡Ganaron todas las zonas!</p>
//                 )
//               } else {
//                 // 🤝 Empate entre algunas zonas
//                 return (
//                   <>
//                     <p className='fs-1'>Ganaron las zonas:</p>
//                     {winningZones.map((w, i) => (
//                       <p key={i} className='fs-1'>Zona {getZoneLabel(w.name)}</p>
//                     ))}
//                   </>
//                 )
//               }
//             })()}
//           </motion.div>
//         </>
//       )}
//     </div>
//   )
// }




















// // 📌 Diccionario para traducir nombres internos a nombres amigables
// const zonesTemplate = [
//   { name: "Zona Laboratorio Tequis", text: "Tequis" },
//   { name: "Zona Canels", text: "Canels" },
//   { name: "Zona Seguros El Potosí", text: "Seguros El Potosí" },
//   { name: "Zona Red Cola", text: "Red Cola" },
//   { name: "Zona Tecate", text: "Tecate" },
//   { name: "Zona Moctezuma", text: "Moctezuma" },
//   { name: "Zona Nissan", text: "Nissan" },
//   { name: "Zona Mobil", text: "Mobil" }
// ]

// // 🔍 Buscar nombre amigable a partir del nombre interno
// const getZoneLabel = (zoneName) => {
//   const match = zonesTemplate.find(z =>
//     z.name.toLowerCase().includes(zoneName.toLowerCase())
//   )
//   return match ? match.text : zoneName
// }

// // 🎯 Extraer datos reales del payload
// const extractFinalResults = (payload) => {
//   const points = payload?.data?.[0]?.CalculatedPointsByZone || {}
//   return Object.entries(points).map(([name, value]) => ({ name, value }))
// }

// // 🎲 Generar datos aleatorios para animación
// const generateAnimatedData = (zones) => {
//   return zones.map(zone => ({
//     name: zone.name,
//     value: Math.floor(Math.random() * 20) + 1
//   }))
// }

// // 🏆 Obtener zonas con el valor más alto
// const getWinningZones = (zones) => {
//   if (!zones || zones.length === 0) return []
//   const maxPoints = Math.max(...zones.map(z => z.value))
//   return zones.filter(z => z.value === maxPoints)
// }

// // 💡 Componente principal
// const Results = ({ payload }) => {
//   const [animatedData, setAnimatedData] = useState([])
//   const [finalResults, setFinalResults] = useState([])
//   const [showWinner, setShowWinner] = useState(false)
//   const [winningZones, setWinningZones] = useState([])
//   const [showChart, setShowChart] = useState(true)

//   useEffect(() => {
//     // 1️⃣ Extraemos los datos reales al cargar
//     const realResults = extractFinalResults(payload)
//     setFinalResults(realResults)

//     // 2️⃣ Animación con datos aleatorios
//     let intervalId = null
//     if (realResults.length > 0) {
//       setAnimatedData(generateAnimatedData(realResults)) // inicia animación
//       intervalId = setInterval(() => {
//         setAnimatedData(generateAnimatedData(realResults))
//       }, 100)
//     }

//     // 3️⃣ Después de 3 segundos, se muestra el resultado real
//     const revealTimeout = setTimeout(() => {
//       clearInterval(intervalId)
//       setAnimatedData(realResults) // actualiza gráfica con datos reales

//       const showWinnerTimeout = setTimeout(() => {
//         setShowChart(false)
//         const winners = getWinningZones(realResults) // asegúrate de usar datos REALES
//         setWinningZones(winners)
//         setShowWinner(true)
//       }, 500)

//       return () => clearTimeout(showWinnerTimeout)
//     }, 3000)

//     // Cleanup en desmontaje
//     return () => {
//       clearInterval(intervalId)
//       clearTimeout(revealTimeout)
//     }
//   }, [payload])

//   return (
//     <div className="Results__FullScreenOverlay">
//       {/* 📊 Gráfica animada */}
//       {showChart && (
//         <div className="w-full h-96">
//           <ResponsiveContainer width="100%" height="100%">
//             <BarChart data={animatedData}>
//               <CartesianGrid strokeDasharray="3 3" stroke="#ccc" />
//               <XAxis dataKey="name" stroke="#fff" tickFormatter={getZoneLabel} />
//               <YAxis stroke="#fff" />
//               <Tooltip formatter={(value, name, props) => [`${value} pts`, getZoneLabel(props.payload.name)]} />
//               <Bar dataKey="value" fill="#ff4d4f" />
//             </BarChart>
//           </ResponsiveContainer>
//         </div>
//       )}

//       {/* 🏆 Ganador */}
//       {showWinner && winningZones.length > 0 && (
//         <>
//           <Confetti width={window.innerWidth} height={window.innerHeight} recycle={false} numberOfPieces={300} />
//           <motion.div
//             initial={{ scale: 0 }}
//             animate={{ scale: 1.2 }}
//             transition={{ duration: 0.6, type: 'spring' }}
//             className="text-4xl font-bold text-center mt-4"
//           >
//             <p><i className="bi bi-trophy fs-1"></i></p>
//             {(() => {
//               if (winningZones.length === 1) {
//                 // 🎯 Solo una zona con puntaje máximo
//                 return (
//                   <p className='fs-1'>
//                     La zona ganadora es...<br />
//                     Zona {getZoneLabel(winningZones[0].name)}
//                   </p>
//                 )
//               } else if (winningZones.length === finalResults.length) {
//                 // 🟰 Todas empataron
//                 return (
//                   <p className='fs-1'>¡Ganaron todas las zonas!</p>
//                 )
//               } else {
//                 // 🤝 Empate entre algunas zonas
//                 return (
//                   <>
//                     <p className='fs-1'>Ganaron las zonas:</p>
//                     {winningZones.map((w, i) => (
//                       <p key={i} className='fs-1'>Zona {getZoneLabel(w.name)}</p>
//                     ))}
//                   </>
//                 )
//               }
//             })()}
//           </motion.div>
//         </>
//       )}
//     </div>
//   )
// }










// const zonesTemplate = [
//     { name: "Zona Laboratorio Tequis", text: "Tequis" },
//     { name: "Zona Canels", text: "Canels" },
//     { name: "Zona Seguros El Potosí", text: "Seguros El Potosí" },
//     { name: "Zona Red Cola", text: "Red Cola" },
//     { name: "Zona Tecate", text: "Tecate" },
//     { name: "Zona Moctezuma", text: "Moctezuma" },
//     { name: "Zona Nissan", text: "Nissan" },
//     { name: "Zona Mobil", text: "Mobil" }
// ]

// const getZoneLabel = (zoneName) => {
//   const match = zonesTemplate.find(z =>
//     z.name.toLowerCase().includes(zoneName.toLowerCase()))
//   return match ? match.text : zoneName
// }

// const getWinningZones = (zones) => {
//   if (!zones || zones.length === 0) return []

//   const maxPoints = Math.max(...zones.map(z => z.value))
//   return zones.filter(z => z.value === maxPoints)
// }

// const getRandomizedData = (zones) => {
//   return zones.map(zone => ({
//     name: zone.name,
//     value: Math.floor(Math.random() * 20) + 1 // rango más amplio
//   }))
// }

// const extractZonesFromPayload = (payload) => {
//   const zonePoints = payload?.data?.[0]?.CalculatedPointsByZone || {}
//   return Object.entries(zonePoints).map(([name, value]) => ({ name, value }))
// }

// const getWinnerZone = (zones) => {
//   if (!zones || zones.length === 0) return null
//   return zones.reduce((acc, curr) => (curr.value > acc.value ? curr : acc))
// }

// const Results = ({ payload }) => {
//   const [animatedData, setAnimatedData] = useState([])
//   const [showWinner, setShowWinner] = useState(false)
//   const [winner, setWinner] = useState(null)
//   const [showChart, setShowChart] = useState(true)
//   const [zones, setZones] = useState([])

//   useEffect(() => {
//     const zonesData = extractZonesFromPayload(payload)
//     setZones(zonesData)

//     let intervalId
//     if (zonesData.length > 0) {
//       setAnimatedData(getRandomizedData(zonesData)) // inicializa con datos aleatorios
//       intervalId = setInterval(() => {
//         setAnimatedData(getRandomizedData(zonesData))
//       }, 100)
//     }

//     const revealTimeout = setTimeout(() => {
//       clearInterval(intervalId)
//       setAnimatedData(zonesData)
//       const showWinnerTimeout = setTimeout(() => {
//         setShowChart(false)
//         const winners = getWinningZones(zonesData)
//         setWinner(winners)
//         setShowWinner(true)
//       }, 500)

//       return () => clearTimeout(showWinnerTimeout)
//     }, 3000)

//     return () => {
//       clearInterval(intervalId)
//       clearTimeout(revealTimeout)
//     }
//   }, [payload])

  
//   return (
//     <>
//       <div className="Results__FullScreenOverlay">
//           {showChart && (
//             <div className="w-full h-96">
//               <ResponsiveContainer width="100%" height="100%">
//                 <BarChart data={animatedData}>
//                   <CartesianGrid strokeDasharray="3 3" stroke="#ccc" />
//                   <XAxis dataKey="name" stroke="#fff" />
//                   <YAxis stroke="#fff" />
//                   <Tooltip />
//                   <Bar dataKey="value" fill="#ff4d4f" />
//                 </BarChart>
//               </ResponsiveContainer>
//             </div>
//           )}
          
//           {showWinner && winner && (
//             <>
//               <Confetti width={window.innerWidth} height={window.innerHeight} recycle={false} numberOfPieces={300} />
//               <motion.div
//                 initial={{ scale: 0 }}
//                 animate={{ scale: 1.2 }}
//                 transition={{ duration: 0.6, type: 'spring' }}
//                 className="text-4xl font-bold text-center mt-4"
//               >
//                 <p><i className="bi bi-trophy fs-1"></i></p>

//                 {winner.length === 1 ? (
//                   <p className='fs-1'>La zona ganadora es...<br />Zona {getZoneLabel(winner[0].name)}</p>
//                 ) : winner.length === zones.length ? (
//                   <p className='fs-1'>¡Ganaron todas las zonas!</p>
//                 ) : (
//                   <>
//                     <p className='fs-1'>Ganaron las zonas:</p>
//                     {winner.map((w, i) => (
//                       <p key={i} className='fs-1'>Zona {getZoneLabel(w.name)}</p>
//                     ))}
//                   </>
//                 )}
//               </motion.div>
//             </>
//           )}
//       </div>
//     </>
//   )
// }






























// import React, { useEffect, useState } from 'react'
// import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
// import { motion } from 'framer-motion'
// import Confetti from 'react-confetti'


// const getRandomizedData = (zones) => {
//   return zones.map(zone => ({
//     name: zone.name,
//     value: Math.floor(Math.random() * 10) + 1
//   }))
// }

// const extractZonesFromPayload = (payload) => {
//   const zonePoints = payload?.data?.[0]?.CalculatedPointsByZone || {}
//   return Object.entries(zonePoints).map(([name, value]) => ({ name, value }))
// }

// const getWinnerZone = (zones) => {
//   if (!zones || zones.length === 0) return null
//   return zones.reduce((acc, curr) => (curr.value > acc.value ? curr : acc))
// }

// const Results = ({ payload }) => {
//   const [animatedData, setAnimatedData] = useState([])
//   const [showWinner, setShowWinner] = useState(false)
//   const [winner, setWinner] = useState(null)
//   const [showChart, setShowChart] = useState(true)
//   const [zones, setZones] = useState([])

//   useEffect(() => {
//     const zonesData = extractZonesFromPayload(payload)
//     setZones(zonesData)

//     let intervalId
//     if (zonesData.length > 0) {
//       intervalId = setInterval(() => {
//         setAnimatedData(getRandomizedData(zonesData))
//       }, 100)
//     }

//     const revealTimeout = setTimeout(() => {
//       clearInterval(intervalId)
//       setAnimatedData(zonesData)
//       setTimeout(() => {
//         setShowChart(false)
//         const winnerZone = getWinnerZone(zonesData)
//         setWinner(winnerZone)
//         setShowWinner(true)
//       }, 500)
//     }, 3000)

//     return () => {
//       clearInterval(intervalId)
//       clearTimeout(revealTimeout)
//     }
//   }, [payload])

  
//   return (
//     <>
//       <div className="Results__FullScreenOverlay">
//           {showChart && (
//             <div className="w-full h-96">
//               <ResponsiveContainer width="100%" height="100%">
//                 <BarChart data={animatedData}>
//                   <CartesianGrid strokeDasharray="3 3" stroke="#ccc" />
//                   <XAxis dataKey="name" stroke="#fff" />
//                   <YAxis stroke="#fff" />
//                   <Tooltip />
//                   <Bar dataKey="value" fill="#ff4d4f" />
//                 </BarChart>
//               </ResponsiveContainer>
//             </div>
//           )}

//           {showWinner && winner && (
//             <>
//               <Confetti width={window.innerWidth} height={window.innerHeight} recycle={false} numberOfPieces={300} />
//               <motion.h1
//                 initial={{ scale: 0 }}
//                 animate={{ scale: 1.5 }}
//                 transition={{ duration: 0.6, type: 'spring' }}
//                 className="text-4xl font-bold text-center mt-4"
//               >
//                 <p><i className="bi bi-trophy fs-1"></i></p>
//                 <p>La zona ganadora es...</p>
//                 <p>Zona {winner.name}</p>
//               </motion.h1>
//             </>
//           )}
//       </div>
//     </>
//   )
// }

// export { Results };